import { post, get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

// File upload response from MCP proxy
export interface MCPFileUploadResponse {
  uri: string;
  size: number;
  mimetype: string;
  filename: string;
}

// Server-resolved system prompt view returned by the admin-only GET /mcp/system-prompt
// endpoint (the prompt itself is server-authoritative and never ships in the bundle).
export interface ResolvedSystemPrompt {
  source: 'db-default' | 'hardcoded-fallback';
  template: string;
  resolved: string;
  /** Model configured on the record chat resolved to; null = router default. */
  model: string | null;
  onboardingGuidance: string;
}

// Attached file info for chat messages
export interface AttachedFile {
  uri: string;
  filename: string;
  size: number;
  mimetype: string;
}

// A model option the server offers. `id` is what the client sends back on a chat request;
// `name` is the picker label. For normal users both are the opaque codename — the vendor and
// tier names live only in the lambda (GET /mcp/models), never in this bundle.
export interface MCPModelOption {
  id: string;
  name: string;
  codename: string;
}

// Server-owned model catalog. `tiers` is populated for admins only and carries the real tier ids
// the System Prompts page and PromptDebugPanel pin; it comes back empty for everyone else.
export interface MCPModelCatalog {
  models: MCPModelOption[];
  default: string;
  tiers: MCPModelOption[];
  surfacePinned: boolean;
}

// Sentinel for the admin model picker: run whatever the chat surface is configured for.
export const MODEL_SURFACE_DEFAULT = '';

export const FALLBACK_CLIENT_MODEL = 'gregoire';

/** Any model id the router serves — a client codename or an admin-only tier. '' means "the surface's model". */
export type ChatModelId = string;

// Codename the project chat runs under. Pinned by design (not user-configurable); the lambda maps
// it to the real model, so no vendor name is needed here.
export const PROJECT_CHAT_MODEL = 'gregoire';
export const PROJECT_CHAT_MODEL_CODENAME = 'Gregoire';

// Display codename for a model id, using the server catalog. Falls back to the raw id so a
// conversation recorded before the catalog loaded still renders something.
export const codenameForModel = (id: string, catalog: MCPModelCatalog | null): string =>
  [...(catalog?.models ?? []), ...(catalog?.tiers ?? [])].find((m) => m.id === id)?.codename || id;

// Shown when the AI model returns no text (e.g. it ran tools but produced no
// final answer). Used by both the sync path and the async subscription path so
// the user sees actionable copy instead of a blank message bubble.
export const EMPTY_AI_RESPONSE_MESSAGE =
  'The MCP server returned an empty response. Please try again.';

/**
 * Normalize AI message text so empty/whitespace responses fall back to a
 * user-facing message instead of rendering an empty bubble. User messages are
 * returned unchanged.
 */
export function normalizeAiMessageText(
  text: string | null | undefined,
  isUser: boolean
): string {
  if (isUser) {
    return text ?? '';
  }
  return text && text.trim().length > 0 ? text : EMPTY_AI_RESPONSE_MESSAGE;
}

export interface MCPChatRequest {
  model: string;
  prompt: string;
  serverId: string;
}

export interface MCPChatResponse {
  text: string;
  model: string;
  toolsCount: number;
  rounds: number;
  timestamp: string;
  executionTimeMs: number;
  prompt: string;
  serverId: string;
  sessionId?: string; //session ID for conversation tracking
  inputTokens?: number;
  outputTokens?: number;
}

export class MCPService {
  // Store the last enhanced prompt for debugging
  static lastEnhancedPrompt: string = '';
  // Store custom system prompt override for testing
  static customSystemPrompt: string | null = null;

  // Warmup function to pre-initialize the Lambda to avoid cold starts
  static async warmup(): Promise<void> {
    try {
      const restOperation = get({
        apiName: 'mcpProxyApi',
        path: '/mcp/warmup',
      });
      await restOperation.response;
    } catch (error) {
      console.warn('MCP Lambda warmup failed:', error);
    }
  }

  // Helper function to clean text and remove problematic formatting
  private static cleanText(text: string): string {
    return text
      // Replace multiple newlines with single space
      .replace(/\n+/g, ' ')
      // Replace multiple spaces with single space
      .replace(/\s+/g, ' ')
      // Remove special characters that might cause issues, but preserve URI chars and parens
      .replace(/[^\w\s.,!?:/()\-]/g, '')
      // Trim whitespace
      .trim();
  }

  static async sendMessage(
    model: ChatModelId,
    prompt: string,
    serverId: string | null = null,
    isFirstMessage: boolean = false,
    chatHistory: Array<{text: string, isUser: boolean}> = [],
    sessionId: string | null = null // session ID for conversation tracking
  ): Promise<{ text: string; sessionId?: string; inputTokens?: number; outputTokens?: number }> {
    // Clean the input prompt first
    const cleanPrompt = MCPService.cleanText(prompt);

    // Build structured prompt
    let enhancedPrompt = cleanPrompt;

    // Trim whitespace and normalize multiple spaces for logging consistency
    enhancedPrompt = enhancedPrompt.replace(/\s+(?!\n)/g, ' ').trim();

    // Store for debugging access
    MCPService.lastEnhancedPrompt = enhancedPrompt;

    // Build request body - don't pass serverId so gateway returns ALL server tools
    const requestBody: any = {
      model: model,
      prompt: cleanPrompt, // Use clean prompt for actual request from user to MCP server to avoid issues
    };

    // The system prompt is resolved SERVER-side (issue #694) — the proxy ignores anything a
    // non-admin sends. Only forward an admin's in-memory override when one is set.
    if (MCPService.customSystemPrompt) {
      requestBody.systemPrompt = MCPService.customSystemPrompt;
    }

    // Only pass serverId if explicitly provided (for backward compatibility)
    if (serverId) {
      requestBody.serverId = serverId;
    }
    // Only pass sessionId if provided (for conversation tracking)
    if (sessionId) {
      requestBody.sessionId = sessionId;
    }
    
    try {
      // Use Amplify REST API to call the MCP proxy Lambda
      const restOperation = post({
        apiName: 'mcpProxyApi',
        path: '/mcp/chat',
        options: {
          body: requestBody,
        },
      });

      const response = await restOperation.response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (await response.body.json()) as any;

      const normalizeResponseText = (value: unknown): string => {
        if (typeof value !== 'string') {
          return String(value);
        }
        const stripToolOutput = (text: string): string => {
          return text
            .replace(/```json[\s\S]*?```/gi, '')
            .replace(/```[\s\S]*?(gridded__|hydat__|postgres__|analysis__|skills__|context__|userdata__)[\s\S]*?```/gi, '')
            .replace(/^Tool Output:\s*$/gim, '')
            .replace(/^Here are the available fields.*$/gim, '')
            .replace(/^The query was:.*$/gim, '');
        };
        const cleaned = value
          // Strip stray system-style instruction line if it leaks into output.
          .replace(/^If the user's query cannot be answered, explain why\.\s*/i, '')
          // Remove leaked CSV formatting instruction line.
          .replace(/^If the user asked for a plot, provide the data in CSV format, enclosed in <csv><\/csv> tags\.\s*/i, '')
          // Remove leaked chain-of-thought or analysis blocks.
          .replace(/<thought>[\s\S]*?<\/thought>/gi, '')
          .replace(/^\s*Data analysis:\s*$/gim, '')
          .replace(/^\s*Start your response immediately with the content\.?\s*$/gim, '')
          .replace(
            /^\s*If the user has not provided enough information,\s*or if you need to ask follow up questions,\s*please do so\.?\s*If the function results provide an answer to the user's question,\s*provide that answer\.?\s*$/gim,
            ''
          );

        return stripToolOutput(cleaned).replace(/^\s+/, '');
      };
      

      // Helper to parse token counts from various possible formats
      const parseTokenCount = (value: unknown): number | undefined => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          return value;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) {
            return undefined;
          }
          const parsed = Number(trimmed);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return undefined;
      };

      const inputTokens =
        parseTokenCount(data?.inputTokens) ??
        parseTokenCount(data?.input_tokens) ??
        parseTokenCount(data?.tokenUsage?.promptTokens) ??
        parseTokenCount(data?.tokenUsage?.prompt_tokens) ??
        parseTokenCount(data?.usage?.inputTokens) ??
        parseTokenCount(data?.usage?.input_tokens) ??
        parseTokenCount(data?.usage?.promptTokens) ??
        parseTokenCount(data?.usage?.prompt_tokens) ??
        parseTokenCount(data?.usageMetadata?.promptTokenCount) ??
        parseTokenCount(data?.usage_metadata?.prompt_token_count);

      const outputTokens =
        parseTokenCount(data?.outputTokens) ??
        parseTokenCount(data?.output_tokens) ??
        parseTokenCount(data?.tokenUsage?.responseTokens) ??
        parseTokenCount(data?.tokenUsage?.response_tokens) ??
        parseTokenCount(data?.usage?.outputTokens) ??
        parseTokenCount(data?.usage?.output_tokens) ??
        parseTokenCount(data?.usage?.completionTokens) ??
        parseTokenCount(data?.usage?.completion_tokens) ??
        parseTokenCount(data?.usageMetadata?.candidatesTokenCount) ??
        parseTokenCount(data?.usage_metadata?.candidates_token_count);

      // Handle the actual response format from your MCP server
      const responseSessionId =
        data?.sessionId ?? data?.session_id ?? data?.sessionID ?? undefined;

      if (data.text !== undefined) {
        if (typeof data.text === 'string' && data.text.trim().length === 0) {
          return {
            text: EMPTY_AI_RESPONSE_MESSAGE,
            sessionId: responseSessionId,
            inputTokens,
            outputTokens,
          };
        }
        return { text: normalizeResponseText(data.text), sessionId: responseSessionId, inputTokens, outputTokens };
      } else if (data.response) {
        return { text: normalizeResponseText(data.response), sessionId: responseSessionId, inputTokens, outputTokens };
      } else if (data.message) {
        return { text: normalizeResponseText(data.message), sessionId: responseSessionId, inputTokens, outputTokens };
      } else if (data.content) {
        return { text: normalizeResponseText(data.content), sessionId: responseSessionId, inputTokens, outputTokens };
      } else if (typeof data === 'string') {
        return { text: normalizeResponseText(data), sessionId: responseSessionId, inputTokens, outputTokens };
      } else {
        console.error('Unexpected response format:', data);
        return { text: 'Unable to parse response from AI model.', sessionId: responseSessionId, inputTokens, outputTokens };
      }
    } catch (error) {
      console.error('MCP Service error:', error);
      
      // Handle timeout specifically
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('Request timed out after 120 seconds');
        
        // If we have chat history, try again without it (timeout might be due to long prompt)
        if (chatHistory.length > 0) {
          console.log('Retrying without chat history due to timeout...');
          return MCPService.sendMessage(model, prompt, serverId, isFirstMessage, [], sessionId);
        }
        
        throw new Error('Request timed out. The AI model is taking too long to respond.');
      }
      
      // If we have chat history and got a 500 error, try again without history
      if (chatHistory.length > 0 && error instanceof Error && error.message.includes('Server error (5')) {
        console.log('Retrying without chat history due to server error...');
        return MCPService.sendMessage(model, prompt, serverId, isFirstMessage, [], sessionId);
      }

      // Handle session expiration - retry without sessionId to start a fresh session
      // Only retry for errors that likely indicate session issues, not network/server failures
      // (which are already handled above)
      if (sessionId && error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        const isLikelySessionError =
          errorMsg.includes('session') ||
          errorMsg.includes('not found') ||
          errorMsg.includes('expired') ||
          errorMsg.includes('404') ||
          errorMsg.includes('invalid');

        if (isLikelySessionError) {
          console.log('Session-related error detected, retrying without sessionId:', error.message);
          return MCPService.sendMessage(model, prompt, serverId, isFirstMessage, chatHistory, null);
        }

        // For unknown errors with sessionId, log but don't automatically retry
        // to avoid masking real failures or causing duplicate requests
        console.warn('Error with sessionId present (not retrying - unknown error type):', error.message);
      }
      
      if (error instanceof Error) {
        throw new Error(`Failed to get response: ${error.message}`);
      }
      throw new Error('Failed to get response from AI model');
    }
  }

  /**
   * Send a message asynchronously - Lambda will self-invoke and create the AI message.
   * The response will arrive via GraphQL subscription.
   *
   * The system prompt is resolved SERVER-side (issue #694): the client contributes only
   * additive inputs — persona, the onboarding flag, and project chat's context block. An
   * admin's in-memory override (MCPService.customSystemPrompt) is still forwarded; the
   * proxy ignores it unless the caller really is an Admin.
   *
   * @param model - Codename id from the server catalog
   * @param prompt - The user's message/prompt
   * @param conversationId - The conversation ID (required for async flow)
   * @param opts.sessionId - Session ID for conversation continuity
   * @param opts.persona - User persona line, interpolated into the server-resolved prompt
   * @param opts.onboarding - Server appends onboarding guidance (chat-tour first message)
   * @param opts.projectContext - Project chat's <project_context> block, appended server-side
   * @param opts.debug - Admin-only: stream per-round reasoning as <thinking> blocks
   * @returns Promise that resolves when Lambda acknowledges the request
   */
  static async sendMessageAsync(
    model: ChatModelId,
    prompt: string,
    conversationId: string | null,
    opts: {
      sessionId?: string | null;
      persona?: string;
      onboarding?: boolean;
      projectContext?: string;
      debug?: boolean;
      modelOverride?: ChatModelId;
    } = {}
  ): Promise<{ status: string; conversationId: string }> {
    const { sessionId = null, persona, onboarding = false, projectContext, debug = false, modelOverride } = opts;

    // Clean the input prompt
    const cleanPrompt = MCPService.cleanText(prompt);

    // Build request body with conversationId for async flow
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestBody: any = {
      model,
      prompt: cleanPrompt,
      conversationId, // Required for async flow
    };

    if (persona) {
      requestBody.persona = persona;
    }

    if (onboarding) {
      requestBody.onboarding = true;
    }

    if (projectContext) {
      requestBody.projectContext = projectContext;
    }

    // Admin in-memory override (PromptDebugPanel). Ignored server-side for non-admins.
    if (MCPService.customSystemPrompt) {
      requestBody.systemPrompt = MCPService.customSystemPrompt;
    }

    if (sessionId) {
      requestBody.sessionId = sessionId;
    }

    // Admin debug mode: asks the router to stream per-round reasoning, surfaced as
    // collapsible <thinking> blocks. Sent in body (not header) to survive the proxy.
    if (debug) {
      requestBody.debug = true;
    }

    // Admin A/B model pin (PromptDebugPanel). Absent means "run the surface's model"; ignored
    // server-side for non-admins.
    if (modelOverride) {
      requestBody.modelOverride = modelOverride;
    }

    try {
      // Get the user's Cognito JWT to forward to MCP Router
      const session = await fetchAuthSession();
      const userAuthToken = session?.tokens?.idToken?.toString();

      // Pass token in body (not header) to avoid CORS issues with custom headers
      if (userAuthToken) {
        requestBody.userAuthToken = userAuthToken;
      }

      const restOperation = post({
        apiName: 'mcpProxyApi',
        path: '/mcp/chat',
        options: {
          body: requestBody,
        },
      });

      const response = await restOperation.response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (await response.body.json()) as any;

      // Check for error in response body (defensive check for 2xx with error payload)
      if (data.error) {
        throw new Error(data.error);
      }

      // Lambda returns { status: 'processing', conversationId } for async flow
      if (data.status === 'processing') {
        return {
          status: data.status,
          conversationId: data.conversationId,
        };
      }

      // Fallback: sync mode response
      return {
        status: 'completed',
        conversationId: conversationId || '',
      };
    } catch (error) {
      console.error('MCP sendMessageAsync error:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to send message: ${error.message}`);
      }
      throw new Error('Failed to send message');
    }
  }

  /**
   * Admin-only: fetch the server-resolved system prompt (the prompt no longer exists in the
   * frontend bundle — issue #694). Used by PromptDebugPanel for its preview/editor seed.
   */
  static async fetchResolvedSystemPrompt(persona?: string): Promise<ResolvedSystemPrompt> {
    const restOperation = get({
      apiName: 'mcpProxyApi',
      path: '/mcp/system-prompt',
      options: persona ? { queryParams: { persona } } : {},
    });
    const response = await restOperation.response;
    return (await response.body.json()) as unknown as ResolvedSystemPrompt;
  }

  /**
   * Fetch the server-owned model catalog. Normal users get codename-only options; admins also get
   * the real tier list for the System Prompts / debug pickers.
   */
  static async getModels(): Promise<MCPModelCatalog> {
    const restOperation = get({
      apiName: 'mcpProxyApi',
      path: '/mcp/models',
    });
    const response = await restOperation.response;
    return (await response.body.json()) as unknown as MCPModelCatalog;
  }

  // Convert File to base64 string
  private static async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Upload a file to GCS via the MCP proxy Lambda
  // Uses base64 encoding to avoid SigV4 issues with multipart/form-data
  // For chat uploads, conversationId is the conversation. For schedule uploads, pass
  // { kind: 'schedule', scheduleId } and conversationId carries the runnerId — the file then lands
  // in a persistent, schedule-keyed prefix the executor copies into each run's conversation folder.
  static async uploadFile(
    file: File,
    conversationId: string,
    opts?: { kind?: 'chat' | 'schedule'; scheduleId?: string }
  ): Promise<MCPFileUploadResponse> {
    try {
      // Convert file to base64
      const base64Content = await MCPService.fileToBase64(file);

      const restOperation = post({
        apiName: 'mcpProxyApi',
        path: '/mcp/upload',
        options: {
          body: {
            filename: file.name,
            mimetype: file.type || 'application/octet-stream',
            size: file.size,
            content: base64Content,
            conversationId,
            ...(opts?.kind ? { kind: opts.kind } : {}),
            ...(opts?.scheduleId ? { scheduleId: opts.scheduleId } : {}),
          },
        },
      });

      const response = await restOperation.response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (await response.body.json()) as any;

      return {
        uri: data.uri,
        size: data.size,
        mimetype: data.mimetype,
        filename: data.filename,
      };
    } catch (error) {
      console.error('MCP uploadFile error:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to upload file: ${error.message}`);
      }
      throw new Error('Failed to upload file');
    }
  }

  // Download a file from GCS via the MCP proxy Lambda
  static async downloadFile(uri: string): Promise<Blob> {
    try {
      const restOperation = get({
        apiName: 'mcpProxyApi',
        path: '/mcp/download',
        options: {
          queryParams: { uri },
        },
      });

      const response = await restOperation.response;
      const blob = await response.body.blob();
      return blob;
    } catch (error) {
      console.error('MCP downloadFile error:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to download file: ${error.message}`);
      }
      throw new Error('Failed to download file');
    }
  }

  // Helper to trigger browser download of a blob
  static downloadBlobAsFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Format file size for display
  static formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
